import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from 'ddc-intl';
import { setClassNames } from 'ddc-classnames-js';
import { useLabels, useRequestData } from 'wsm-common-data';
import DisclaimerModal from './DisclaimerModal';

function VehicleData({ offer, disclaimer }) {
	const anchorNavigation = '#!';
	const { payment, savings, vehicle } = offer;
	const labels = useLabels();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const { locale, deviceType } = useRequestData();
	const isDesktop = deviceType === 'DESKTOP';
	const showSavings = savings > 10;
	let firstSavingsLine;
	let secondSavingsLine;
	if (showSavings) {
		[firstSavingsLine, secondSavingsLine] = labels
			.get('SAVE_X_PER_MONTH')
			.split('{savings}');
	}
	const vehicleType =
		vehicle.type === 'N' ? labels.get('NEW') : labels.get('PREOWNED');
	const title = `${vehicleType} ${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`;
	const vehicleDetailsClasses = [
		'bg-contrast-low',
		'border',
		isDesktop && 'h-100 d-flex flex-column justify-content-center'
	];
	const titleClasses = [isDesktop ? 'ddc-font-size-large px-4 mb-1' : 'mb-0'];
	const priceClasses = [
		isDesktop ? 'ddc-font-size-xlarge' : 'ddc-font-size-jumbo',
		'mb-4',
		'font-weight-bold',
		'd-inline-block'
	];
	const paymentDataContainerClasses = [
		'payment-data d-flex align-items-center justify-content-center',
		!isDesktop && 'row'
	];
	const savingsClasses = [
		isDesktop ? 'ddc-font-size-base' : 'ddc-font-size-large',
		'font-italic mt-4'
	];
	const viewDisclaimersExtraPadding =
		showSavings && isDesktop ? 'py-3 mb-sm-1' : '';
	const viewDisclaimersContainerClasses = [
		isDesktop ? 'm-auto' : 'my-4',
		viewDisclaimersExtraPadding
	];
	const viewDisclaimersClasses = [
		'btn-link',
		'disclaimer-cursor',
		isDesktop && 'ddc-font-size-small'
	];
	const paymentValueClasses = [
		isDesktop ? 'ddc-font-size-large' : 'ddc-font-size-xxlarge',
		'font-weight-bold',
		'line-height-reset'
	];
	const paymentLabelClasses = [
		isDesktop ? 'ddc-font-size-xsmall' : 'ddc-font-size-small',
		'mt-0'
	];

	const disclaimerLabel =
		disclaimer.offerType === 'P'
			? labels.get('FINANCE_DRIVER_FINANCE_PAYMENT_TOOLTIP_VERBIAGE')
			: labels.get('FINANCE_DRIVER_LEASE_PAYMENT_TOOLTIP_VERBIAGE');

	const disclaimerIntro = disclaimerLabel.split('</strong>')[0] || '';
	let disclaimerBody = disclaimerLabel.split('</strong>')[1] || '';

	Object.entries(disclaimer).forEach(([key, value]) => {
		if (value !== null) {
			disclaimerBody = disclaimerBody.replace(`<%=${key}%>`, value);
		}
	});

	const onDisclaimerButtonClick = () => {
		setIsModalVisible(true);
	};

	const onModalClose = () => {
		setIsModalVisible(false);
	};

	const modalData = {
		title: null,
		intro: disclaimerIntro.replace('<p><strong>', ''),
		body: disclaimerBody.replace('</p>', '')
	};

	return (
		<div className={setClassNames(vehicleDetailsClasses)}>
			<DisclaimerModal
				isOpen={isModalVisible}
				isMobile={!isDesktop}
				onModalClose={onModalClose}
				modalData={modalData}
			/>
			<h2 className={setClassNames(titleClasses)}>{title} </h2>
			<span className={setClassNames(priceClasses)}>
				{formatCurrency(vehicle.price, locale)}
			</span>
			<div className="offer-info-container bg-body mx-4 py-4 px-2">
				<div className={setClassNames(paymentDataContainerClasses)}>
					<div className="col-xs-4 px-lg-0">
						<div
							className={`${setClassNames(paymentValueClasses)}`}
						>
							{formatCurrency(payment.monthly, locale)}
						</div>
						<div
							className={`${setClassNames(paymentLabelClasses)}`}
						>
							{labels.get('PER_MONTH')}
						</div>
					</div>
					<div className="col-xs-4 px-lg-0 border-left border-right">
						<div
							className={`${setClassNames(paymentValueClasses)}`}
						>
							{payment.term}
						</div>
						<div
							className={`${setClassNames(paymentLabelClasses)}`}
						>
							{labels.get('MONTHS')}
						</div>
					</div>
					<div className="col-xs-4 px-lg-0">
						<div
							className={`${setClassNames(paymentValueClasses)}`}
							id="payment-value"
						>
							{disclaimer.offerType === 'P'
								? payment.rate
								: disclaimer.leaseDueAtSigning}
						</div>
						<div
							className={`${setClassNames(paymentLabelClasses)}`}
						>
							{disclaimer.offerType === 'P'
								? labels.get('APR_STAR')
								: labels.get('DUE_AT_SIGNING')}
						</div>
					</div>
				</div>
				{showSavings && (
					<div className={setClassNames(savingsClasses)}>
						{firstSavingsLine}
						<span className="font-weight-bold">
							{formatCurrency(savings, locale)}
						</span>
						{secondSavingsLine}
					</div>
				)}
			</div>
			<div className={setClassNames(viewDisclaimersContainerClasses)}>
				<a
					href={anchorNavigation}
					rel="nofollow"
					onKeyDown={onDisclaimerButtonClick}
					onClick={onDisclaimerButtonClick}
					className={setClassNames(viewDisclaimersClasses)}
					tabIndex={0}
				>
					{labels.get('VIEW_DISCLAIMER')}
				</a>
			</div>
		</div>
	);
}

VehicleData.propTypes = {
	offer: PropTypes.shape({
		payment: PropTypes.shape({
			monthly: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
				.isRequired,
			rate: PropTypes.string.isRequired,
			term: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
				.isRequired
		}),
		savings: PropTypes.number,
		vehicle: PropTypes.shape({
			year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
				.isRequired,
			make: PropTypes.string.isRequired,
			model: PropTypes.string.isRequired,
			trim: PropTypes.string.isRequired,
			type: PropTypes.string.isRequired,
			bodyStyle: PropTypes.string.isRequired,
			price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
				.isRequired
		})
	}),
	disclaimer: PropTypes.shape({
		loanCreditScore: PropTypes.string,
		leaseCreditScore: PropTypes.string,
		APR: PropTypes.string,
		term: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		leaseTerm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		monthlyPaymentPerThousand: PropTypes.string,
		downPayment: PropTypes.string,
		lenderLegalName: PropTypes.string,
		leaseLenderLegalName: PropTypes.string,
		loanProgramEndDate: PropTypes.string,
		leaseProgramEndDate: PropTypes.string,
		dealershipName: PropTypes.string,
		offerType: PropTypes.string,
		leaseDueAtSigning: PropTypes.string,
		acquisitionFee: PropTypes.string,
		capCostTotal: PropTypes.string,
		totalMonthlyPayments: PropTypes.string,
		capCostReduction: PropTypes.string,
		leaseSecurityDeposit: PropTypes.string,
		monthlyPayment: PropTypes.string,
		excessMileCharge: PropTypes.string,
		annualMiles: PropTypes.string
	})
};

export default VehicleData;
